import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './ActionSheet.module.css';

interface ActionSheetProps {
  open: boolean;
  onClose: () => void;
  /** Optionaler Titel über den Action-Items. */
  title?: string;
  children: ReactNode;
}

/**
 * Bottom-Sheet, das via Portal an document.body gerendert wird. Schließt
 * bei Klick auf Backdrop, ESC, oder explizitem onClose-Call. Focus wird
 * beim Open auf die Sheet-Wurzel gesetzt; der Caller ist für Focus-Trap
 * innerhalb der Children selbst verantwortlich (typischerweise reicht es,
 * dass interaktive Buttons in der Children sind).
 */
export function ActionSheet({ open, onClose, title, children }: ActionSheetProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  // Auto-Focus + Body-Scroll-Lock NUR beim Öffnen (Transition false→true),
  // abhängig allein von `open`. Würde hier `onClose` mit drinhängen, liefe der
  // Effect bei jedem Eltern-Re-Render (z.B. Chat-Polling, das `onClose` als
  // Inline-Arrow neu erzeugt) erneut — und `focus()` klaut dann den Fokus aus
  // einem Input, wodurch sich auf Mobile nach einem Tastendruck die Tastatur
  // schließt.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    rootRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // ESC-Handler getrennt — hängt an `onClose` und darf ruhig neu gebunden
  // werden, ohne den Fokus anzufassen.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  function onBackdropClick(e: React.MouseEvent) {
    // Nur schließen, wenn der Klick direkt auf das Backdrop ging — Klicks
    // innerhalb des Sheets sollen nicht bubble-up das Schließen triggern.
    if (e.target === e.currentTarget) onClose();
  }

  return createPortal(
    <div className={styles.backdrop} role="presentation" onClick={onBackdropClick}>
      <div
        ref={rootRef}
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-label={title ?? 'Aktionen'}
        tabIndex={-1}
      >
        {title ? <div className={styles.title}>{title}</div> : null}
        {children}
      </div>
    </div>,
    document.body,
  );
}
